import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import socketService from '../services/SocketService';
import ringtone from '../assets/images/new-order.mp3';
import './NewBookingPopup.css';

const CURRENCY = process.env.REACT_APP_CURRENCY || 'AED';

/**
 * Live "New Booking" alert for the admin panel.
 *
 * Listens on the shared admins socket room and rings continuously until the
 * admin acknowledges the alert. Bookings that arrive while a popup is open are
 * queued so none of them is missed.
 */
const NewBookingPopup = () => {
  const [queue, setQueue] = useState([]);
  const audioRef = useRef(null);
  const pendingPlayRef = useRef(false);
  const navigate = useNavigate();

  const booking = queue[0] || null;

  // ── Audio: one looping element reused for every alert ──
  useEffect(() => {
    const audio = new Audio(ringtone);
    audio.loop = true;
    audio.preload = 'auto';
    audioRef.current = audio;
    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  const stopRinging = useCallback(() => {
    pendingPlayRef.current = false;
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, []);

  const startRinging = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().catch(() => {
      // Browsers block autoplay until the page has been interacted with.
      // Remember the intent and start on the next click / key press.
      pendingPlayRef.current = true;
    });
  }, []);

  // Retry blocked playback on the first user gesture after an alert arrives.
  useEffect(() => {
    const resume = () => {
      if (pendingPlayRef.current && audioRef.current) {
        audioRef.current.play().then(() => { pendingPlayRef.current = false; }).catch(() => {});
      }
    };
    window.addEventListener('click', resume);
    window.addEventListener('keydown', resume);
    return () => {
      window.removeEventListener('click', resume);
      window.removeEventListener('keydown', resume);
    };
  }, []);

  // ── Socket: join the admins room and listen for new bookings ──
  useEffect(() => {
    // The server derives who this is from the token sent in the handshake.
    const joinAdminRoom = () => socketService.emit('admin-connected');

    socketService.connect();
    if (socketService.isConnected()) joinAdminRoom();
    // Re-join after any reconnect, otherwise the room membership is lost.
    socketService.socket.on('connect', joinAdminRoom);

    const handleNewBooking = (data) => {
      if (!data) return;
      setQueue((prev) => (prev.some((b) => b._id === data._id) ? prev : [...prev, data]));
    };

    socketService.socket.on('newBooking', handleNewBooking);

    return () => {
      socketService.socket.off('newBooking', handleNewBooking);
      socketService.socket.off('connect', joinAdminRoom);
    };
  }, []);

  // Ring while a booking is waiting to be acknowledged.
  useEffect(() => {
    if (booking) startRinging();
    else stopRinging();
  }, [booking, startRinging, stopRinging]);

  const acknowledge = () => {
    stopRinging();
    setQueue((prev) => prev.slice(1));
  };

  const viewBooking = () => {
    const id = booking?._id;
    acknowledge();
    if (id) navigate(`/admin/bookings?booking=${id}`);
  };

  if (!booking) return null;

  const services = (booking.services || []).map((s) => s.title).filter(Boolean).join(', ');

  return (
    <>
      <div className="new-booking-backdrop" />
      <div className="new-booking-modal" role="dialog" aria-modal="true" aria-label="New booking received">
        <div className="new-booking-card">
          <div className="new-booking-head">
            <span className="new-booking-bell"><i className="bi bi-bell-fill" /></span>
            <div>
              <div className="new-booking-title">New Booking</div>
              <div className="new-booking-code">{booking.booking_code || `#${String(booking._id).slice(-6)}`}</div>
            </div>
            {queue.length > 1 && (
              <span className="new-booking-queue">+{queue.length - 1} more</span>
            )}
          </div>

          <div className="new-booking-body">
            <div className="new-booking-row">
              <span className="new-booking-label"><i className="bi bi-person" />Customer</span>
              <span className="new-booking-value">{booking.user?.name || 'N/A'}</span>
            </div>
            {booking.user?.phone && (
              <div className="new-booking-row">
                <span className="new-booking-label"><i className="bi bi-telephone" />Phone</span>
                <span className="new-booking-value">{booking.user.phone}</span>
              </div>
            )}
            <div className="new-booking-row">
              <span className="new-booking-label"><i className="bi bi-calendar-event" />Date &amp; Time</span>
              <span className="new-booking-value">
                {booking.booking_date ? moment(booking.booking_date).format('DD MMM YYYY') : 'N/A'}
                {booking.booking_time ? ` · ${booking.booking_time}` : ''}
              </span>
            </div>
            {booking.room?.title && (
              <div className="new-booking-row">
                <span className="new-booking-label"><i className="bi bi-door-open" />Room</span>
                <span className="new-booking-value">{booking.room.title}</span>
              </div>
            )}
            {services && (
              <div className="new-booking-row">
                <span className="new-booking-label"><i className="bi bi-list-check" />Service</span>
                <span className="new-booking-value">{services}</span>
              </div>
            )}
            <div className="new-booking-row">
              <span className="new-booking-label"><i className="bi bi-cash-coin" />Amount</span>
              <span className="new-booking-value">
                {CURRENCY} {parseFloat(booking.grand_total || 0).toFixed(2)}
                {booking.payment_by === 'membership' && <span className="new-booking-tag">Membership</span>}
              </span>
            </div>
          </div>

          <div className="new-booking-actions">
            <button type="button" className="btn btn-light flex-fill" onClick={viewBooking}>
              View Booking
            </button>
            <button type="button" className="btn btn-base flex-fill" onClick={acknowledge} autoFocus>
              Okay
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewBookingPopup;
